import * as React from 'react';
import isEmpty from 'lodash/isEmpty';

import PopoverScrollable from 'components/popover-scrollable/PopoverScrollable';

import { __t, applyTranslations } from 'utils/language';

import './ccm-campaign-details-specifics.scss';

applyTranslations(require('./ccm-campaign-details-specifics.i18n.json'));

import type { Campaign } from 'types';

type Props = {
  campaign: Campaign
};

const MSG_KEY = 'ccm_campaign_details_specifics';
const NUM_CHARS_BEFORE_TRIGGERING_TEAM_MEMBERS_POPOVER = 73;

const CcmCampaignDetailsSpecifics: React.FC<Props> = ({
  campaign
}): JSX.Element => {
  const renderCampaignType = (): React.ReactNode => (
    <div className="ccm-campaign-details-specifics__section">
      <div className="ccm-campaign-details-specifics__section-title">
        {__t(`${MSG_KEY}.sections.campaign_type`)}
      </div>
      <div className="ccm-campaign-details-specifics__section-value">
        <span>{campaign.packageName}</span> {__t(`${MSG_KEY}.package`)}
      </div>
    </div>
  );

  const renderMaxOutfits = (): React.ReactNode => (
    <div className="ccm-campaign-details-specifics__section">
      <div className="ccm-campaign-details-specifics__section-title">
        {__t(`${MSG_KEY}.sections.max_outfits`)}
      </div>
      <div className="ccm-campaign-details-specifics__section-value">
        {campaign.maxOutfits}
      </div>
    </div>
  );

  const renderMaxCreators = (): React.ReactNode => (
    <div className="ccm-campaign-details-specifics__section">
      <div className="ccm-campaign-details-specifics__section-title">
        {__t(`${MSG_KEY}.sections.max_creators`)}
      </div>
      <div className="ccm-campaign-details-specifics__section-value">
        {campaign.maxParticipants}
      </div>
    </div>
  );

  const renderNumOutfits = (): React.ReactNode => (
    <div className="ccm-campaign-details-specifics__section">
      <div className="ccm-campaign-details-specifics__section-title">
        {__t(`${MSG_KEY}.sections.num_outfits`)}
      </div>
      <div className="ccm-campaign-details-specifics__section-value">
        {campaign.outfitsPerCreator}
      </div>
    </div>
  );

  const renderTeamMembers = (): React.ReactNode => {
    const managers = campaign.managers || [];
    const names = managers.map(m => m.name);
    const namesString = names.join(', ');

    return (
      <div className="ccm-campaign-details-specifics__section">
        <div className="ccm-campaign-details-specifics__section-title">
          {__t(`${MSG_KEY}.sections.team_members`)}
        </div>
        <div className="ccm-campaign-details-specifics__section-value">
          {
            namesString.length < NUM_CHARS_BEFORE_TRIGGERING_TEAM_MEMBERS_POPOVER
              ? namesString
              : (
                <PopoverScrollable
                  list={names.map(n => ({ text: n }))}
                  size="large"
                  trigger={(
                    <div className="ccm-campaign-details-specifics__section-value">
                      {namesString}
                    </div>
                  )}
                />
              )
          }
        </div>
      </div>
    );
  };

  return (
    <div className="ccm-campaign-details-specifics">
      <div className="ccm-campaign-details-specifics__title">
        {__t(`${MSG_KEY}.title`)}
      </div>
      <div className="ccm-campaign-details-specifics__sections">
        {renderCampaignType()}
        {renderMaxOutfits()}
        {renderMaxCreators()}
        {renderNumOutfits()}
        {!isEmpty(campaign.managers) && renderTeamMembers()}
      </div>
    </div>
  );
};

export default CcmCampaignDetailsSpecifics;
